import { useEffect, useLayoutEffect, useState } from 'react';

import {
  COUNTRY_ILLUSTRATIONS,
  type CountryIllustration,
} from '../constants/countryIllustrations';

// useLayoutEffect on the client (apply cached country before paint, no flash);
// useEffect on the server, where useLayoutEffect is a no-op and warns.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const CACHE_KEY = 'commenda_country_code';
const CACHE_TIMESTAMP_KEY = 'commenda_country_code_ts';
const CACHE_TTL = 7 * 24 * 60 * 60 * 1000;
const API_TIMEOUT = 3000;
const DEFAULT_COUNTRY = 'US';

function getCachedCountryCode(): string | null {
  if (typeof window === 'undefined') return null;

  const cached = localStorage.getItem(CACHE_KEY);
  const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

  if (!cached || !timestamp) return null;

  const age = Date.now() - parseInt(timestamp, 10);
  if (age > CACHE_TTL) {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    return null;
  }

  return cached;
}

function setCachedCountryCode(code: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(CACHE_KEY, code);
  localStorage.setItem(CACHE_TIMESTAMP_KEY, String(Date.now()));
}

async function fetchCountryCode(): Promise<string> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

  try {
    // country.is — free, HTTPS, CORS, no API key; returns { ip, country }.
    const res = await fetch('https://api.country.is/', {
      signal: controller.signal,
    });

    if (!res.ok) return DEFAULT_COUNTRY;

    const data = await res.json();
    return data.country || DEFAULT_COUNTRY;
  } catch {
    return DEFAULT_COUNTRY;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function getCountryIllustration(
  countryCode?: string
): CountryIllustration {
  const normalizedCountryCode = countryCode?.toUpperCase() || DEFAULT_COUNTRY;

  return (
    COUNTRY_ILLUSTRATIONS[normalizedCountryCode] ??
    COUNTRY_ILLUSTRATIONS.DEFAULT ?? { image: '/setup/login_illustration.svg' }
  );
}

/** Country-specific auth-page illustration, IP-geolocated and cached for 7 days. */
export function useCountryIllustration(): {
  countryCode: string;
  illustration: CountryIllustration;
  isResolved: boolean;
} {
  // SSR can't read localStorage; `isResolved` gates the image so it never flashes.
  const [countryCode, setCountryCode] = useState<string>(DEFAULT_COUNTRY);
  const [isResolved, setIsResolved] = useState<boolean>(false);

  useIsomorphicLayoutEffect(() => {
    const cached = getCachedCountryCode();
    if (cached) {
      setCountryCode(cached);
      setIsResolved(true);
      return;
    }

    let cancelled = false;

    void fetchCountryCode().then((code) => {
      if (!cancelled) {
        setCountryCode(code);
        setCachedCountryCode(code);
        setIsResolved(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    countryCode,
    illustration: getCountryIllustration(countryCode),
    isResolved,
  };
}
